import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type DownloadAllModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onDownload: (params: DownloadAllValues) => Promise<void>;
  initialValues: Partial<DownloadAllValues>;
};

export type DownloadAllValues = {
  companyPayment: Option<string> | null;
  agency: Option<string> | null;
  taxYear: number;
  taxPeriod: number;
  sendTo: string;
};

const defaultInitialValues: Partial<DownloadAllValues> = {
  companyPayment: null,
  agency: null,
  sendTo: '',
};

const DownloadAllModal: React.FC<DownloadAllModalProps> = ({
  isOpen,
  toggle,
  onDownload,
  initialValues = {},
}: DownloadAllModalProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The modal stays mounted between opens, so re-apply initial values on each
  // open — otherwise a page-level filter change (e.g. tax year) after first
  // mount would leave the form holding a stale value while the selects
  // display the new one.
  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        ...defaultInitialValues,
        ...initialValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, form]);

  const onFinish: FormProps['onFinish'] = (values) => {
    setIsSubmitting(true);
    onDownload(values)
      .then(() => {
        toggle();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.downloadAll']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.download'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          ...defaultInitialValues,
          ...initialValues,
        }}
      >
        <Form.Item
          label={employerLabels.label}
          name="companyPayment"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            placeholder={messages['form.selectCompany']}
            style={{ width: '100%' }}
            onChangeValue={(value: any) => {
              form.setFieldValue('companyPayment', value);
            }}
          />
        </Form.Item>
        {!isBureau && (
          <Form.Item
            label={messages['common.agency']}
            name="agency"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder={messages['form.pleaseSelectAgency']}
              style={{ width: '100%' }}
              onChangeValue={(value: any) => {
                form.setFieldValue('agency', value);
              }}
            />
          </Form.Item>
        )}
        <Form.Item
          label={messages['common.taxYear']}
          name="taxYear"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <TaxYearSelect
            initValue={initialValues?.taxYear}
            placeholder={messages['common.selectTaxYear']}
            onFilter={(e) => form.setFieldValue('taxYear', e.taxYear)}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.taxPeriod']}
          name="taxPeriod"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <TaxWeekMonthSelect
            placeholder={messages['form.select']}
            onFilter={(e) => form.setFieldValue('taxPeriod', e.taxPeriod)}
            paymentFrequency="WEEKLY"
          />
        </Form.Item>
        <Form.Item
          label={messages['common.sendTo']}
          name="sendTo"
          rules={[
            { required: true, message: messages['form.pleaseEnter'] },
            { type: 'email', message: messages['form.enterValidEmail'] },
          ]}
        >
          <AppInput
            placeholder={messages['form.enterEmail']}
            allowCapitalize={false}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default DownloadAllModal;
